import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { authApi } from "../api/endpoints";
import { AuthLayout } from "../components/Layout";
import { Alert, Button, TextField } from "../components/ui";
import { useDocumentTitle, useMutation } from "../hooks/useApi";

export function PasswordResetRequestPage() {
  useDocumentTitle("Mot de passe oublié");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);

  const request = useMutation(() => authApi.requestPasswordReset(email.trim()), {
    onSuccess: (response) => setSent(response.message),
  });

  return (
    <AuthLayout
      title="Mot de passe oublié"
      intro="Nous vous enverrons un lien permettant de choisir un nouveau mot de passe."
      footer={<Link to="/connexion">Retour à la connexion</Link>}
    >
      {request.error && (
        <Alert tone="critical" title="Demande impossible">
          {request.error.message}
        </Alert>
      )}
      {sent && (
        <Alert tone="success" title="Demande enregistrée">
          {sent}
        </Alert>
      )}
      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          void request.run();
        }}
        noValidate
      >
        <TextField
          label="Adresse e-mail"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={request.fieldErrors.email}
        />
        <Button type="submit" variant="primary" block loading={request.pending}>
          Envoyer le lien
        </Button>
      </form>
    </AuthLayout>
  );
}

export function PasswordResetConfirmPage() {
  useDocumentTitle("Nouveau mot de passe");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [done, setDone] = useState<string | null>(null);

  const confirm = useMutation(
    () => authApi.confirmPasswordReset({ token, new_password: password }),
    { onSuccess: (response) => setDone(response.message) },
  );

  const mismatch =
    confirmation.length > 0 && confirmation !== password
      ? "Les deux mots de passe ne correspondent pas."
      : undefined;

  if (!token) {
    return (
      <AuthLayout
        title="Lien incomplet"
        footer={<Link to="/mot-de-passe-oublie">Demander un nouveau lien</Link>}
      >
        <Alert tone="critical" title="Jeton manquant">
          Ce lien ne contient pas de jeton de réinitialisation. Demandez un nouvel
          envoi.
        </Alert>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Mot de passe modifié">
        <Alert tone="success" title="C'est fait">
          {done}
        </Alert>
        <Button
          variant="primary"
          block
          className="mt-4"
          onClick={() => navigate("/connexion", { replace: true })}
        >
          Se connecter
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choisir un nouveau mot de passe"
      intro="Par sécurité, toutes vos sessions seront déconnectées."
    >
      {confirm.error && (
        <Alert tone="critical" title="Modification impossible">
          {confirm.error.message}
        </Alert>
      )}
      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!mismatch) void confirm.run();
        }}
        noValidate
      >
        <TextField
          label="Nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={confirm.fieldErrors.new_password}
          hint="Au moins 12 caractères, avec au moins 3 types de caractères différents."
        />
        <TextField
          label="Confirmation"
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          error={mismatch}
        />
        <Button
          type="submit"
          variant="primary"
          block
          loading={confirm.pending}
          disabled={!password || Boolean(mismatch)}
        >
          Enregistrer le nouveau mot de passe
        </Button>
      </form>
    </AuthLayout>
  );
}
